// supabase/functions/_shared/nda-status.ts
//
// The vocabulary of an NDA e-signature request, and nothing else.
//
// ZERO IMPORTS, no `Deno.` globals, no side effects — the house rule for a
// `*-status.ts` module, so that the same file is imported by the migration test
// (vitest, Node), the portal chunk and the public signing page (Vite, by
// relative path), and five edge functions (Deno). Every CHECK list in migration
// 249 is rendered FROM these arrays, and `tests/nda-migration.test.ts` reads the
// migration back off disk to prove it — the fence against
// 124_hit_list_status_vocab.sql, where the UI wrote a status the CHECK
// disallowed and the feature persisted zero rows. Here a lost row is worse than
// a lost list entry: it is a seller who signed a contract Flow never recorded.
//
// Two lists are DUPLICATED rather than imported, because this module may not
// import: the interest letter's nine recipient roles and the financials desk's
// staff pair. `tests/nda-status.test.ts` asserts equality with both sources.

// ── the request ──────────────────────────────────────────────────────────────

/** `requested` is FIRST because it is the migration DEFAULT: a row written by
 *  anything that forgets this column must sit visibly on the desk, never send.
 *  Seal and filing progress are deliberately NOT statuses (spec §4.2) — they are
 *  derived from `sealed_at`/`seal_error`/`filed_at`/`filing_error` wherever they
 *  are shown, so a failed seal never hides the fact that both parties signed. */
export const NDA_STATUSES = [
  'requested', 'sending', 'sent', 'send_failed', 'viewed', 'signed', 'completed',
  'completed_offline', 'declined', 'changes_requested', 'voided', 'expired',
] as const;
export type NdaStatus = typeof NDA_STATUSES[number];

export const NDA_STATUS_LABELS: Record<NdaStatus, string> = {
  requested: 'Not sent yet',
  sending: 'Sending',
  sent: 'Sent for signature',
  send_failed: 'Send failed',
  viewed: 'Opened by seller',
  signed: 'Seller signed — to countersign',
  completed: 'Executed',
  completed_offline: 'Signed on paper',
  declined: 'Seller declined',
  changes_requested: 'Changes requested',
  voided: 'Voided',
  expired: 'Link expired',
};

/** The desk's working slice, the one-open-request-per-recipient index and the
 *  researcher's open-request cap share this list. `signed` is open: Endeavor
 *  still owes its countersignature. `changes_requested` is open: staff owe the
 *  seller an answer, then a Resend or a Void. */
export const NDA_OPEN_STATUSES = [
  'requested', 'sending', 'sent', 'send_failed', 'viewed', 'signed', 'changes_requested',
] as const;
export const NDA_CLOSED_STATUSES = [
  'completed', 'completed_offline', 'declined', 'voided', 'expired',
] as const;

/** What `nda_apply_seller_signature` admits. The same pair gates `decline` and
 *  `request_changes` on the public page. */
export const NDA_SIGNABLE = ['sent', 'viewed'] as const;
/** What `nda_apply_countersignature` admits. */
export const NDA_COUNTERSIGNABLE = ['signed'] as const;
/** What the desk's Resend (rotate the link + send again) may act on: anything
 *  the seller has not yet signed. Never `signed` — that would rotate a link the
 *  seller has already used and kill their route to the executed copy.
 *  `nda-send`'s rotate/resend claim reads THIS list — wider than §4.10's
 *  first-send claim (`requested | send_failed | changes_requested`) because a
 *  Resend on a `sent`/`viewed` row is legitimate; a hand-typed copy of §4.10's
 *  list there would make every such Resend fail to claim. */
export const NDA_RESENDABLE = [
  'requested', 'send_failed', 'sent', 'viewed', 'changes_requested',
] as const;
/** Void is "any open status" (Appendix E: "no way to void after the fact"). */
export const NDA_VOIDABLE = NDA_OPEN_STATUSES;

export function isNdaOpen(status: string): boolean {
  return (NDA_OPEN_STATUSES as readonly string[]).includes(status);
}
export function ndaStatusLabel(status: string): string {
  return (NDA_STATUS_LABELS as Record<string, string>)[status] ?? status;
}

/** Who filed the request. No DEFAULT: the insert always says which. */
export const NDA_REQUESTED_BY_KINDS = ['researcher', 'staff'] as const;
export type NdaRequestedByKind = typeof NDA_REQUESTED_BY_KINDS[number];
export const NDA_REQUESTED_BY_KIND_LABELS: Record<NdaRequestedByKind, string> = {
  researcher: 'Researcher',
  staff: 'Staff',
};

/** Who sends the link. `email` is FIRST because it is the migration DEFAULT —
 *  the financials rule. `link_only` mints the link and emails nobody; the
 *  plaintext is returned once, to whoever minted it (tokens are hashed at rest). */
export const NDA_DELIVERIES = ['email', 'link_only'] as const;
export type NdaDelivery = typeof NDA_DELIVERIES[number];
export const NDA_DELIVERY_LABELS: Record<NdaDelivery, string> = {
  email: 'Flow emails them the NDA',
  link_only: "I'll send the link myself",
};

/** `none` is FIRST because it is the migration DEFAULT (decision 16: the
 *  emailed one-time code is off by default and arrives in Phase 2). */
export const NDA_IDENTITY_CHECKS = ['none', 'email_code'] as const;
export type NdaIdentityCheck = typeof NDA_IDENTITY_CHECKS[number];
export const NDA_IDENTITY_CHECK_LABELS: Record<NdaIdentityCheck, string> = {
  none: 'None',
  email_code: 'Emailed one-time code',
};

/** How a signature was adopted. Every kind arrives at the server as PNG bytes —
 *  a typed signature is rendered to PNG in the browser, an upload re-encoded —
 *  so the kind is provenance for the certificate, never a format switch. */
export const NDA_SIGNATURE_KINDS = ['drawn', 'typed', 'uploaded'] as const;
export type NdaSignatureKind = typeof NDA_SIGNATURE_KINDS[number];
export const NDA_SIGNATURE_KIND_LABELS: Record<NdaSignatureKind, string> = {
  drawn: 'Drawn',
  typed: 'Typed',
  uploaded: 'Uploaded image',
};

/** The interest letter's nine roles, DUPLICATED because this module may not
 *  import. `tests/nda-status.test.ts` asserts equality with
 *  `LETTER_RECIPIENT_ROLES`. The labels are the financials form's, because the
 *  NDA form is a copy of that form. */
export const NDA_RECIPIENT_ROLES = [
  'park_manager', 'management_company', 'owner_staff', 'owner_direct',
  'family_member', 'attorney', 'accountant', 'listing_broker', 'other',
] as const;
export type NdaRecipientRole = typeof NDA_RECIPIENT_ROLES[number];
export const NDA_RECIPIENT_ROLE_LABELS: Record<NdaRecipientRole, string> = {
  park_manager: 'Park manager',
  management_company: 'Management company',
  owner_staff: "Owner's staff",
  owner_direct: 'The owner',
  family_member: 'Family member',
  attorney: 'Attorney',
  accountant: 'Accountant / bookkeeper',
  listing_broker: 'Listing broker',
  other: 'Other',
};

// ── the event log ────────────────────────────────────────────────────────────

/** Spec §4.3, in that order. Append a new type at the END: the migration's
 *  CHECK is rendered from this array and the certificate prints these names. */
export const NDA_EVENT_TYPES = [
  'created', 'template_rendered', 'sent', 'send_failed', 'link_rotated',
  'link_revoked', 'link_extended', 'viewed', 'identity_code_sent', 'identity_verified',
  'disclosure_accepted', 'seller_signed', 'pre_signed', 'countersigned', 'completed',
  'executed_rendered', 'certificate_generated', 'sealed', 'seal_failed', 'completion_email_sent',
  'completion_email_failed', 'filed', 'filing_failed', 'downloaded', 'declined',
  'changes_requested', 'changes_responded', 'voided', 'expired', 'reminder_sent', 'nudged',
  'offline_recorded', 'note',
] as const;
export type NdaEventType = typeof NDA_EVENT_TYPES[number];

export const NDA_EVENT_TYPE_LABELS: Record<NdaEventType, string> = {
  created: 'Request created',
  template_rendered: 'Agreement rendered for sending',
  sent: 'Sent to the seller',
  send_failed: 'Send failed',
  link_rotated: 'New signing link issued',
  link_revoked: 'Signing link revoked',
  link_extended: 'Signing link extended',
  viewed: 'Opened by the seller',
  identity_code_sent: 'Identity code sent',
  identity_verified: 'Identity verified',
  disclosure_accepted: 'Electronic-records disclosure accepted',
  seller_signed: 'Seller signed',
  pre_signed: 'Endeavor pre-signed',
  countersigned: 'Endeavor countersigned',
  completed: 'Agreement completed',
  executed_rendered: 'Executed agreement rendered',
  certificate_generated: 'Certificate of completion generated',
  sealed: 'Sealed',
  seal_failed: 'Seal failed',
  completion_email_sent: 'Executed copy emailed to the seller',
  completion_email_failed: 'Executed-copy email failed',
  filed: 'Filed to SharePoint',
  filing_failed: 'Filing failed',
  downloaded: 'Downloaded by the seller',
  declined: 'Seller declined',
  changes_requested: 'Seller requested changes',
  changes_responded: 'Staff responded to the change request',
  voided: 'Voided',
  expired: 'Link expired',
  reminder_sent: 'Reminder sent',
  nudged: 'Researcher nudged',
  offline_recorded: 'Paper-signed copy recorded',
  note: 'Note',
};

/** Who caused an event. `system` is the cron sweep and the seal pipeline. */
export const NDA_ACTOR_KINDS = ['seller', 'staff', 'researcher', 'system'] as const;
export type NdaActorKind = typeof NDA_ACTOR_KINDS[number];
export const NDA_ACTOR_KIND_LABELS: Record<NdaActorKind, string> = {
  seller: 'Seller',
  staff: 'Staff',
  researcher: 'Researcher',
  system: 'Flow',
};

// ── notifications ────────────────────────────────────────────────────────────

/** The twelve `notification_type` values of spec §5, spelled once here so the
 *  writers (`nda-core`, the Phase 2 cron) and the icon map in
 *  `NotificationsDropdown.tsx` cannot disagree. `notifications.notification_type`
 *  carries no CHECK, so a misspelling would land a bell with the fallback icon
 *  and no one would notice — this list is the only fence.
 *
 *  Plus a thirteenth, appended: `nda_closed`, the desk's "Close with note"
 *  (spec §4.12: the note "reaches the researcher verbatim"). §5's table has no
 *  row for it, but the note needs a bell and a type, and a type spelled only
 *  inside portal-admin would be exactly the drift this list exists to stop.
 *
 *  And a fourteenth, for the same reason: `nda_changes_answered`, the desk's
 *  answer to a seller's change request, told to the researcher verbatim —
 *  on a `link_only` request Flow emails nobody, so she is the only way the
 *  answer reaches the seller (spec §4.12's "note to the seller"). */
export const NDA_NOTIFICATION_TYPES = [
  'nda_sent', 'nda_send_failed', 'nda_signed', 'nda_completed',
  'nda_declined', 'nda_changes_requested', 'nda_expiring', 'nda_expired', 'nda_countersign_due',
  'nda_seal_failed', 'nda_filing_failed', 'nda_term_ending',
  'nda_closed', 'nda_changes_answered',
] as const;
export type NdaNotificationType = typeof NDA_NOTIFICATION_TYPES[number];

// ── limits ───────────────────────────────────────────────────────────────────

export const NDA_LIMITS = {
  /** A signing link lives 30 days from mint (decision 9)… */
  LINK_DAYS: 30,
  /** …and is extended to 30 days after completion so the seller can fetch the
   *  executed copy from the page. */
  DOWNLOAD_DAYS: 30,
  OPEN_PER_RESEARCHER: 10,
  SENDS_PER_RESEARCHER_PER_DAY: 10,
  /** Caps on every typed field, in characters, applied after trim. Keys are the
   *  column (or seller field) names. A handler holding a plain `string` column
   *  name reads a cap through `ndaFieldMax(col)` — the `as const` literal keys
   *  refuse a `string` index under strict TS. */
  FIELD_MAX: {
    // The seller's blanks — Appendix A's caps, verbatim.
    seller_legal_name: 200,
    seller_signer_name: 120,
    seller_signer_title: 120,
    seller_notice_address_1: 200,
    seller_notice_address_2: 200,
    seller_notice_attn: 120,
    // Chosen here (the spec leaves them open):
    /** The composed phrase, or the `other` kind's custom text. "a Delaware
     *  statutory trust acting by its trustee" is 48; 120 is the signer caps'. */
    seller_entity_phrase: 120,
    /** Matches `request_financials`' recipient_email cap (200); no deliverable
     *  address is longer. */
    seller_notice_email: 200,
    /** The "not right? tell us" box — a sentence or two about the property. */
    property_correction: 2000,
    /** Why the seller declined; free text that reaches staff and researcher verbatim. */
    declined_reason: 2000,
    /** What the seller wants changed — may quote clauses, so the longer cap. */
    changes_requested_text: 4000,
    /** Staff's answer to a change request, emailed to the seller. */
    changes_response: 4000,
    /** The next four mirror `request_financials` (portal-api/actions-financials.ts)
     *  so the two forms refuse at the same lengths. */
    invitation: 4000,
    researcher_notes: 4000,
    recipient_name: 200,
    recipient_email: 200,
    recipient_title: 200,
    recipient_organization: 200,
    /** Desk-typed reasons and notes; the close note reaches the researcher verbatim. */
    void_reason: 2000,
    outcome_note: 4000,
  },
  /** Minimums the spec states (§4.9, §4.11), after trim — one spelling for the
   *  public page, the handlers and the forms. */
  FIELD_MIN: {
    declined_reason: 3,
    changes_requested_text: 10,
    invitation: 20,
  },
  /** Signature images: PNG only, magic-bytes sniffed, IHDR-checked (decision 7). */
  SIGNATURE_MAX_BYTES: 1_048_576,
  SIGNATURE_MAX_W: 1600,
  SIGNATURE_MAX_H: 600,
  /** `viewed` events recorded per request per day; further opens still stamp
   *  `last_viewed_at`/`view_count` but do not grow the log. */
  VIEWS_RECORDED_PER_DAY: 120,
  DOWNLOADS_PER_DAY: 20,
  /** Automatic seller reminders (Phase 2 cron), one on each listed day. */
  AUTO_REMINDERS: 2,
  REMINDER_DAYS: [3, 7] as readonly number[],
  /** A researcher's "Nudge" is allowed once every three days. */
  NUDGE_SPACING_DAYS: 3,
  COUNTERSIGN_NUDGE_BUSINESS_DAYS: 2,
  /** §9 Term: one year from the Effective Date. */
  TERM_DAYS: 365,
  /** A `sending` claim older than this is a crash the desk may recover — the
   *  financials number (`FINANCIALS_STUCK_SENDING_MINUTES`). */
  STUCK_SENDING_MINUTES: 15,
  /** `sealNda`'s re-claim window: a `seal_started_at` older than this is a crash. */
  STUCK_SEAL_MINUTES: 15,
  /** "Resend my signing email" on the upload page (spec §4.13). */
  UPLOAD_PAGE_RESENDS_PER_DAY: 3,
  /** Every signed storage URL handed out (as-sent, executed, certificate, signatures). */
  SIGNED_URL_MINUTES: 10,
  /** The page's draft autosave is throttled to one save per this many seconds. */
  DRAFT_SAVE_MIN_SECONDS: 2,
} as const;

export type NdaFieldKey = keyof typeof NDA_LIMITS.FIELD_MAX;
/** The cap for a typed field by name, or `undefined` for a name with no cap —
 *  the one spelling for a handler that validates a body field by field. */
export function ndaFieldMax(field: string): number | undefined {
  return (NDA_LIMITS.FIELD_MAX as Record<string, number>)[field];
}

// ── stuck work ───────────────────────────────────────────────────────────────

function olderThan(iso: string | null | undefined, minutes: number, now: Date): boolean {
  // No clock, or one that does not parse, reads as a crash: the row claimed the
  // work and never said when, so nothing will ever release it on its own.
  if (!iso) return true;
  const started = new Date(iso).getTime();
  return Number.isNaN(started) || now.getTime() - started > minutes * 60_000;
}

/** A `sending` row whose claim is more than `STUCK_SENDING_MINUTES` old: the
 *  send crashed between claim and outcome. Exactly the window is still live;
 *  one millisecond past it is stuck — the same `>` as `isFinancialsSendStuck`. */
export function isNdaSendStuck(
  row: { status: string; sending_started_at?: string | null },
  now: Date = new Date(),
): boolean {
  if (row.status !== 'sending') return false;
  return olderThan(row.sending_started_at, NDA_LIMITS.STUCK_SENDING_MINUTES, now);
}

/** A `completed` row that is neither sealed nor failed, whose seal claim is more
 *  than `STUCK_SEAL_MINUTES` old. An UNCLAIMED seal is clocked from
 *  `completed_at` instead: the countersign RPC completes the row a moment before
 *  `sealNda` claims it, and that moment must not read as stuck — but a seal that
 *  was never claimed at all (the call died between the two) must, once the
 *  window passes. A row with `seal_error` is not stuck, it FAILED; the desk
 *  shows that chip instead, and Retry seal serves both. `completed_offline` is
 *  never sealed (no certificate), so it is never stuck.
 *
 *  This is the DESK's display predicate (which chip to show), NOT the server's
 *  claim condition. `sealNda`'s claim and the Phase 2 sweep rule (e) keep the
 *  plan's SQL — `sealed_at is null and (seal_started_at is null or
 *  seal_started_at < now() - 15 min) and status = 'completed'` — which ignores
 *  `seal_error` and so re-claims a failed row; neither may call this helper. */
export function isNdaSealStuck(
  row: {
    status: string;
    sealed_at?: string | null;
    seal_error?: string | null;
    seal_started_at?: string | null;
    completed_at?: string | null;
  },
  now: Date = new Date(),
): boolean {
  if (row.status !== 'completed' || row.sealed_at || row.seal_error) return false;
  return olderThan(row.seal_started_at ?? row.completed_at, NDA_LIMITS.STUCK_SEAL_MINUTES, now);
}

/** Ryan and Jamie — `FINANCIALS_STAFF_RECIPIENTS`, DUPLICATED because this
 *  module may not import (decision 17). The countersigner is added per request
 *  by `notifyNdaParties`, not here. */
export const NDA_STAFF_RECIPIENTS = ['ryan@elevationcg.com', 'jamie@elevationcg.com'] as const;

// ── SQL renderers ────────────────────────────────────────────────────────────

export function sqlInList(values: readonly string[]): string {
  return values.map((v) => `'${v}'`).join(',');
}

/** Every CHECK-constrained column in migration 249, as `table.column`, so the
 *  read-back test can find each list spelled exactly (the
 *  `PORTAL_LETTER_CHECK_COLUMNS` naming — three tables carry checks here). */
export const NDA_CHECK_COLUMNS: Record<string, readonly string[]> = {
  'nda_requests.status': NDA_STATUSES,
  'nda_requests.recipient_role': NDA_RECIPIENT_ROLES,
  'nda_requests.delivery': NDA_DELIVERIES,
  'nda_requests.identity_check': NDA_IDENTITY_CHECKS,
  'nda_requests.seller_signature_kind': NDA_SIGNATURE_KINDS,
  'nda_requests.countersign_signature_kind': NDA_SIGNATURE_KINDS,
  'nda_requests.requested_by_kind': NDA_REQUESTED_BY_KINDS,
  'nda_events.event_type': NDA_EVENT_TYPES,
  'nda_events.actor_kind': NDA_ACTOR_KINDS,
  'staff_signatures.signature_kind': NDA_SIGNATURE_KINDS,
};
